import { SecondFromMidnight, TimeSlot } from "@cervval.js/calendars/src/calendars.definition";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_ITEMS, FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, JUSTIFY_CONTENT, MARGIN, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { ButtonSize, ButtonStyle, CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { showErrorInDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { CjsSmartTable, SmartTableCell } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTable";
import { SmartTableColumn, getSmartTablePropertyColumnsFromPropertyConfigs } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTableColumn.tools";
import { SmartTableStyle } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTableStyle.helper";
import { EDITOR_FACTORY_SERVICE, EditorFactoryService } from "@cervval.js/client-html5-ui/src/editors/editorFactory.service";
import { EditorWithPicker } from "@cervval.js/client-html5-ui/src/editors/editorsWithPicker/editorWithPicker";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ensureLoaded, keyof } from "@cervval.js/common/src/tools/object.tools";
import { HOURS_IN_ONE_DAY, SECONDS_IN_ONE_DAY, SECONDS_IN_ONE_HOUR, SECONDS_IN_ONE_MINUTE } from "@cervval.js/common/src/tools/time.tools";
import { DAYS } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { showHideRowButtonsOnHover } from "@ehealth_tools/client-html-tools/src/tools/smartTable.tools";
import { getTimeSlotPropertyConfig } from "@ehealth_tools/common-booking/src/configs/timeslot.propertyConfig";
import { BookingWeek } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { AbstractClientBookingService } from "../../../../../../services/abstractClientBooking.service";

export interface TimeSlotsEditorOptions {
    currentWeek?: BookingWeek;
    selectedDays: DAYS[];
    onChange?: () => void;
}

const defaultTimeSlotsEditorOptions: TimeSlotsEditorOptions = {
    selectedDays: []
};


ensureLoaded(CjsSmartTable);
ensureLoaded(CjsButton);
@CustomElement({
    name: "timeslots-editor",
    style: require("./timeslotsEditor.scss"),
    template: require("./timeslotsEditor.html"),
    elementCssClasses: [MARGIN.MX_2, FLEX_CONTAINER_COLUMN]
})

export class TimeSlotsEditor extends WebComponent {

    @TypedWebComponentOptions(defaultTimeSlotsEditorOptions)
    public readonly options!: WebComponentOptions<TimeSlotsEditorOptions>;


    constructor(options?: TimeSlotsEditorOptions) {
        super();
        this.options.setWithoutRefresh(options);
    }
    @TypedHtmlElements()
    private _components!: {
        addTimeslot: CjsButton,
        timeslotsSmartTable: CjsSmartTable<TimeSlot>
    };

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;

    @Injected(EDITOR_FACTORY_SERVICE)
    private _editorFactory!: EditorFactoryService;

    private _currentTimeSlots: TimeSlot[] = [];

    /** @override */
    public override init(): void {
        this.style.width = "30%";
        this._components.addTimeslot.options.setAndRefreshIfChanged({
            icon: { name: "fa-plus" },
            iconMode: "only",
            cssClasses: [W_100, "addTimeSlot"]
        });

        this._components.addTimeslot.clickEvent.on(() => {
            const week = this.options.currentWeek;
            if (week == null) { return; }
            const timeslots = [...this._currentTimeSlots];
            const lastFinish = timeslots[timeslots.length - 1]?.finish ?? 8 * SECONDS_IN_ONE_HOUR;
            const nextStart = (lastFinish + 1) as SecondFromMidnight;
            const nextFinish = (lastFinish + SECONDS_IN_ONE_HOUR < SECONDS_IN_ONE_DAY ? lastFinish + SECONDS_IN_ONE_HOUR : SECONDS_IN_ONE_DAY) as SecondFromMidnight;
            if (nextStart < SECONDS_IN_ONE_DAY) {
                this._bookingService.setTimeSlots([...timeslots, { start: nextStart, finish: nextFinish }], week, this.options.selectedDays).then(_ => { this.refresh(); this.options.onChange?.(); }).catch(showErrorInDialog);
            }
        });

        const columns: SmartTableColumn<TimeSlot>[] = getSmartTablePropertyColumnsFromPropertyConfigs(getTimeSlotPropertyConfig());
        // Override default colum
        const timeCols = columns.filter(v => v.uid === keyof<TimeSlot>("start") || v.uid === keyof<TimeSlot>("finish"));
        for (const timeCol of timeCols) {
            timeCol.renderCell = (c) => {
                let hours = Math.floor(c.value / SECONDS_IN_ONE_HOUR);
                let minutes = Math.round((c.value - hours * SECONDS_IN_ONE_HOUR) / SECONDS_IN_ONE_MINUTE);
                // Sometimes, minutes can be superior or equal to 60, so we change the display of hour and minute
                if (minutes >= SECONDS_IN_ONE_MINUTE) { minutes = minutes - SECONDS_IN_ONE_MINUTE; hours++; }
                // Sometimes hours can be superior or equal to 24, if it's the case, we force the value
                if (hours >= HOURS_IN_ONE_DAY) { hours = 0; }
                // We pad value inferior to 10 with a 0
                return `${hours < 10 ? "0" + hours : hours}:${minutes < 10 ? "0" + minutes : minutes}`;
            };
            timeCol.getCellEditor = (c) => {
                if (c.column.propertyConfig) {
                    const editor = this._editorFactory.newEditor(c.column.propertyConfig);
                    if (editor instanceof EditorWithPicker) {
                        return editor;
                    }
                }
                return undefined;
            };
        }
        columns.push({
            uid: "actions",
            sortDisabled: true,
            width: "10%",
            filterDisabled: true,
            renderCell: (cell: SmartTableCell<TimeSlot>) => {
                const currentWeek = this.options.currentWeek;
                if (currentWeek) {
                    const deleteButton = new CjsButton({
                        icon: { name: "fa-trash" },
                        cssStyle: ButtonStyle.DANGER,
                        size: ButtonSize.SMALL,
                        iconMode: "only"
                    });
                    deleteButton.clickEvent.on(() => {
                        const timeslot = cell.rowItemData;
                        const cpyTimeSlots: TimeSlot[] = [];
                        if (timeslot) {
                            for (const ts of this._currentTimeSlots) {
                                if (ts !== timeslot) {
                                    cpyTimeSlots.push(ts);
                                }
                            }
                        }
                        this._bookingService.setTimeSlots(cpyTimeSlots, currentWeek, this.options.selectedDays).then(_ => { this.refresh(); this.options.onChange?.(); }).catch(showErrorInDialog);
                    });
                    const row = cell.row.tr;
                    if (row) {
                        showHideRowButtonsOnHover(row, [deleteButton]);
                    }
                    return createHtmlElement("div", {
                        cssClass: [FLEX_CONTAINER, W_100, FLEX_ITEM_AUTO, JUSTIFY_CONTENT.CENTER, ALIGN_ITEMS.CENTER],
                        innerHTML: [deleteButton]
                    });
                } else {
                    return null;
                }
            }
        });

        this._components.timeslotsSmartTable.options.setWithoutRefresh({
            columns,
            isCellEditable: (c) => c.column.uid !== "actions"
        });

        this._components.timeslotsSmartTable.cellEditedValueEvent.on(e => {
            const timeslot = e.detail.cell.rowItemData;
            const cpyTimeSlots: TimeSlot[] = [];
            const currentWeek = this.options.currentWeek;
            if (timeslot && currentWeek) {
                for (const ts of this._currentTimeSlots) {
                    if (ts === timeslot) {
                        const duration = timeslot.finish - timeslot.start;
                        const newStart = e.detail.cell.column.uid === "start" ? e.detail.newValue : timeslot.start;
                        const newFinish = e.detail.cell.column.uid === "finish" ?
                            e.detail.newValue <= timeslot.start ? timeslot.start + 1 : e.detail.newValue
                            : newStart + duration;
                        cpyTimeSlots.push({
                            start: newStart,
                            // Guard to prevent finish to go beyond the current day
                            finish: newFinish > SECONDS_IN_ONE_DAY ? SECONDS_IN_ONE_DAY : newFinish
                        });
                    } else {
                        cpyTimeSlots.push(ts);
                    }
                }
                this._bookingService.setTimeSlots(cpyTimeSlots, currentWeek, this.options.selectedDays).then(_ => {
                    this.refresh(); 
                    this.options.onChange?.(); 
                }).catch(showErrorInDialog);
            }
        });
    }

    /** */
    public override refresh(): void {
        this._refreshSmartTable();
        this._refreshButton();
    }

    /** */
    private _refreshSmartTable(): void {
        const currentWeek = this.options.currentWeek;
        this._currentTimeSlots = [];
        let same = true;
        if (currentWeek) {
            const week = currentWeek.week;
            for (let d = 0; d < this.options.selectedDays.length; d++) {
                const day = this.options.selectedDays[d];
                const weekTimeslots = week[day]?.timeslots;
                if (weekTimeslots) {
                    for (const weekTimeslot of weekTimeslots) {
                        if (!this._currentTimeSlots.some(v => v.start === weekTimeslot.start && v.finish === weekTimeslot.finish)) {
                            this._currentTimeSlots.push(weekTimeslot);
                            same = d > 0 ? false : same;
                        }
                    }
                    if (d > 0 && this._currentTimeSlots.length && weekTimeslots.length === 0) {
                        same = false;
                    }
                } else if (d > 0 && this._currentTimeSlots.length) {
                    same = false;
                }
            }
            this._currentTimeSlots.sort((a, b) => a.start - b.start);
            this._components.timeslotsSmartTable.options.setWithoutRefresh({
                cssStyle: same ? SmartTableStyle.NONE : SmartTableStyle.WARNING
            });
            this._components.timeslotsSmartTable.setData({ data: this._currentTimeSlots });
            this._components.timeslotsSmartTable.refresh();
        }
    }

    /** */
    private _refreshButton(): void {
        toggleClass(this._components.addTimeslot.components.button, this.options.currentWeek == null, "disabled");
        this._components.addTimeslot.refresh();
    }


}